import Typography from '@ixigo/iui/Typography';
import cn from '@repo/shared/utils/cn';
import type { BaseCabItemOption } from '@/api';
import { Avatar } from './Avatar';
import { Detail, type DetailProps } from './Detail';

export interface CabCardProps extends BaseCabItemOption {
	/** Whether this cab option is currently selected (affects border styling) */
	isSelected?: boolean;
	/** URL of the cab icon image (should be a valid HTTP/HTTPS URL) */
	iconUrl: string;

	burnOpted?: DetailProps['burnOpted'];
}

const CapacityMap: Record<
	keyof BaseCabItemOption['capacity'],
	'PAX' | 'BAGGAGE'
> = {
	travellers: 'PAX',
	luggage: 'BAGGAGE',
};

/**
 * Item component displays a complete cab option with icon, details, and capacity information.
 * This is a reusable component for showing individual cab selections in the review page.
 *
 * @component
 * @param {string} iconUrl - URL of the cab icon image (should be a valid HTTP/HTTPS URL)
 * @param {string} vehicleType - Type of vehicle (e.g., "SUV", "Hatchback", "Sedan")
 * @param {string} fuelType - Type of fuel for the fuel chip (e.g., "Petrol", "Diesel", "CNG")
 * @param {boolean} [isSelected=false] - Whether this cab option is currently selected (affects border styling)
 * @param {CabNote | null} note - Optional promotional note with text and type for highlighting
 * @param {CabCapacity} capacity - Passenger and luggage capacity information (passengers and luggage counts)
 * @param {object} pricing - Pricing information with original and optional discounted price
 * @param {string} model - Vehicle model description (e.g., "Toyota Innova", "Maruti Swift")
 */
export const CabCard = ({
	iconUrl,
	vehicleType,
	fuelType,
	isSelected = false,
	note,
	capacity,
	pricing,
	model,
	burnOpted,
}: CabCardProps) => {
	const capacityData = Object.entries(capacity)
		.map(([key, value]) => {
			const capacityType = CapacityMap[key as keyof CabCardProps['capacity']];
			return {
				type: capacityType,
				title: value,
			};
		})
		.filter((item) => item.type); // Remove items with invalid types

	const isNoteAvailable = note?.text && note?.type;

	return (
		<div
			className={cn(
				isNoteAvailable &&
					'rounded-[15px] bg-gradient-to-b from-new-moderate to-new-subtle p-[5px] pt-0',
			)}
		>
			<Typography
				variant="body-xs"
				className={cn(
					isNoteAvailable ? `text-${note.type} text-center py-0.5` : 'hidden',
				)}
			>
				{note?.text}
			</Typography>
			<div
				className={cn(
					'flex items-center border gap-x-10 px-2.5 py-2 rounded-[15px] bg-primary',
					isSelected ? 'border-selection-outline' : 'border-tertiary',
				)}
			>
				<Avatar
					iconUrl={iconUrl}
					vehicleType={vehicleType}
					fuelType={fuelType}
				/>
				<Detail
					capacityData={capacityData}
					vehicleType={vehicleType}
					model={model}
					pricing={pricing}
					burnOpted={burnOpted}
				/>
			</div>
		</div>
	);
};
